// ============================================================
// Zeus v2 — Shared Type Definitions
// These types are the contract between frontend, server, and PocketBase.
// All agents code against these interfaces.
// ============================================================

import type { RecordModel } from 'pocketbase'

// --- Base ---

export interface BaseRecord extends RecordModel {
  id: string
  created: string
  updated: string
}

// --- Tenants ---

export type TenantStatus =
  | 'provisioning'
  | 'active'
  | 'suspended'
  | 'dormant'
  | 'archived'
  | 'provision_failed'

export type TlsStatus = 'pending' | 'live' | 'failed'

export interface TTenant extends BaseRecord {
  // Identity
  name: string
  slug: string
  subdomain: string

  // Business info
  legal_name: string
  industry: string
  website: string
  primary_contact_name: string
  primary_contact_email: string
  primary_contact_phone: string

  // Status
  status: TenantStatus
  provision_error: string

  // Infrastructure
  droplet_id: string
  droplet_ip: string
  droplet_private_ip: string
  volume_id: string
  agi_volume_id: string
  n8n_volume_id: string
  dns_record_id: string
  wildcard_dns_record_id: string
  tenant_url: string
  tls_status: TlsStatus

  // Versions (only updated on successful health check)
  web_app_version: string
  openclaw_version: string
  pocketbase_tag: string
  caddy_tag: string
  n8n_version: string

  // n8n provisioning state — `''` (not provisioned), 'provisioned', 'unprovisioned' (failed)
  n8n_status: string

  /** OpenRouter key hash (returned by OpenRouter management API at create time).
   *  Used to PATCH limit / DELETE the key. The actual secret lives in
   *  `tenant_secrets.OPENROUTER_API_KEY` (not recoverable from OpenRouter). */
  openrouter_key_hash: string
  /** Per-tenant monthly OpenRouter spend cap in USD. Default 200. */
  openrouter_budget_dollars: number

  // Lifecycle
  last_known_good_provision: string // relation ID
  owner: string // relation ID

  // Soft delete
  is_deleted: boolean
  deleted_at: string
}

// --- Tenant Provisions ---

export type ProvisionType = 'initial' | 'update' | 'redeploy' | 'teardown'
export type ProvisionStatus = 'running' | 'complete' | 'failed' | 'rolled_back'

export interface ProvisionChanges {
  openclaw?: string // e.g., "1.4.8 → 1.5.0"
  web_app?: string // e.g., "v0.7.3 → v0.7.4"
  pocketbase?: string // e.g., "0.36.4-b3 → 0.36.8-b5"
  caddy?: string // e.g., "cad-b2 → cad-b3"
  new_vars?: string[]
  removed_vars?: string[]
}

export interface TTenantProvision extends BaseRecord {
  tenant: string // relation ID
  job: string // relation ID — the job that triggered this provision
  provision_type: ProvisionType
  status: ProvisionStatus
  is_active: boolean
  changes: ProvisionChanges
  started_at: string
  completed_at: string
}

// --- Tenant Provision Env ---

export interface TTenantProvisionEnv extends BaseRecord {
  provision: string // relation ID
  env_snapshot: Record<string, string> // encrypted full key-value map
}

// --- Tenant Secrets ---

export type SecretSource = 'generated' | 'global' | 'openrouter'

export interface TTenantSecret extends BaseRecord {
  tenant: string // relation ID
  key_name: string
  key_value: string // encrypted at rest
  source: SecretSource
  generated_at: string
  last_used_at: string
}

// --- Provision Logs ---

export type LogLevel = 'info' | 'warn' | 'error' | 'milestone' | 'debug'

export interface TProvisionLog extends BaseRecord {
  tenant: string // relation ID
  provision: string // relation ID
  timestamp: string
  level: LogLevel
  message: string
  milestone: string
  milestone_started: string
  milestone_ended: string
  milestone_delta_ms: number
  metadata: Record<string, unknown>
  elapsed_ms: number
}

// --- Health Checks ---

export type OverallHealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unreachable'

export interface EndpointCheck {
  status: 'up' | 'down'
  response_ms?: number
  http_code?: number
  error?: string
  details?: string
}

export interface TlsCheck {
  status: 'valid' | 'invalid' | 'expired' | 'pending'
  expires_at?: string
  days_remaining?: number
}

export interface ResourceUsage {
  cpu_percent?: number
  memory_percent?: number
  disk_percent?: number
}

export interface THealthCheck extends BaseRecord {
  tenant: string // relation ID
  checked_at: string
  is_healthy: boolean
  overall_status: OverallHealthStatus

  infrastructure: {
    droplet_reachable: EndpointCheck
    docker_running: EndpointCheck
    volumes_mounted: EndpointCheck
  }

  services: {
    caddy: EndpointCheck
    pocketbase: EndpointCheck
    frontend: EndpointCheck
    action_runner: EndpointCheck
    openclaw: EndpointCheck
    /** Only checked when tenant.n8n_status === 'provisioned'; absent otherwise. */
    n8n?: EndpointCheck
  }

  tls: TlsCheck
  resource_usage: ResourceUsage
  notes: string
}

// --- Jobs ---

export type JobType = 'provisioning' | 'teardown' | 'delete' | 'redeploy' | 'update' | 'n8n_bootstrap' | 'n8n_rotate_key'
export type JobStatus = 'queued' | 'running' | 'complete' | 'failed' | 'cancelled'

export interface TJob extends BaseRecord {
  title: string
  description: string
  job_type: JobType
  status: JobStatus
  tenant: string // relation ID
  provision: string // relation ID
  input_data: Record<string, unknown>
  output_data: Record<string, unknown>
  error_log: string
  created_at: string // explicit (PB 0.36 base collections don't return system created)
  started_at: string
  completed_at: string
  is_deleted: boolean
  deleted_at: string
}

// --- Audit Log ---

export interface TAuditLog extends BaseRecord {
  tenant: string // relation ID
  job: string // relation ID
  event: string
  actor: string
  details: string
  payload: Record<string, unknown>
  timestamp: string // explicit timestamp (PB 0.36 base collections don't return created)
  is_deleted: boolean
  deleted_at: string
}

// --- Platform Secrets ---

export interface TPlatformSecret extends BaseRecord {
  key: string
  value: string
  description: string
}

// --- Settings ---

export interface TSetting extends BaseRecord {
  key: string
  value: string
  description: string
}

// --- Users ---

export interface TUser extends BaseRecord {
  name: string
  email: string
  avatar_url: string
}

// --- Hook System ---

export type HookEvent =
  // Provisioning
  | 'provision.started'
  | 'provision.env_validated'
  | 'provision.env_validation_failed'
  | 'provision.droplet_created'
  | 'provision.dns_configured'
  | 'provision.droplet_ready'
  | 'provision.stack_deployed'
  | 'provision.health_check_passed'
  | 'provision.health_check_failed'
  | 'provision.tls_live'
  | 'provision.tls_failed'
  | 'provision.completed'
  | 'provision.failed'
  // Updates
  | 'update.started'
  | 'update.env_validated'
  | 'update.env_validation_failed'
  | 'update.image_pulled'
  | 'update.container_restarted'
  | 'update.health_check_passed'
  | 'update.health_check_failed'
  | 'update.rollback_started'
  | 'update.rollback_completed'
  | 'update.completed'
  | 'update.failed'
  // Lifecycle
  | 'tenant.created'
  | 'tenant.suspended'
  | 'tenant.resumed'
  | 'tenant.teardown_started'
  | 'tenant.teardown_completed'
  | 'tenant.dormant'
  | 'tenant.delete_started'
  | 'tenant.delete_completed'
  | 'tenant.redeploy_started'
  | 'tenant.redeploy_completed'
  // Health
  | 'health.check_completed'
  | 'health.status_changed'
  | 'health.tenant_unreachable'
  // Logging
  | 'log.entry'
  | 'log.milestone'

export interface HookContext {
  tenant?: TTenant
  provision?: TTenantProvision
  job?: TJob
  timestamp: Date
  data: Record<string, unknown>
}

export type HookHandler = (context: HookContext) => Promise<void>

// --- Wizard Payload ---

export interface WizardPayload {
  identity: {
    legal_name: string
    slug: string
  }
  contact: {
    name: string
    email: string
    phone: string
    website?: string
    industry?: string
  }
  auth: {
    mode: 'domain' | 'user'
    allowed_domain?: string
    invited_emails?: string[]
  }
  versions: {
    web_app: string
    openclaw: string
    pocketbase: string
    caddy: string
    n8n?: string
  }
  options: {
    skip_onboarding: boolean
    /** Per-tenant OpenRouter monthly spend cap in USD. Defaults to 200 server-side. */
    openrouter_budget_dollars?: number
  }
}

// --- Env Schema ---

export type EnvVarSource = 'provisioning_input' | 'global' | 'generated' | 'computed'

export interface EnvVarDefinition {
  name: string
  description: string
  source: EnvVarSource
  required: boolean
  usedBy?: string // which container(s) use this
}

export interface ResolvedEnvVar {
  name: string
  value: string
  source: EnvVarSource
  redacted: boolean
}

// --- Milestones ---

/**
 * Milestone catalog — single source of truth for all provisioning,
 * teardown, update, and redeploy milestones.
 *
 * The arrays below are the AUTHORITATIVE list. The `MilestoneId` union is
 * DERIVED from them via `typeof`, so adding a new milestone is a one-line
 * change. The `logger.milestone()` signature requires a `MilestoneId`, so
 * emit sites get a compile error if they reference a missing id — preventing
 * silent drift between the UI and the workers.
 *
 * To add a new milestone:
 *   1. Add an entry to the appropriate array below
 *   2. Call `logger.milestone('<your_id>', '<Label>')` in the worker
 *   3. TypeScript handles the rest
 */

export interface MilestoneDefinition {
  id: string
  label: string
  order: number
}

export const PROVISION_MILESTONES = [
  { id: 'env_collection_started', label: 'Collect Environment Variables', order: 1 },
  { id: 'openrouter_key_provisioned', label: 'Provision OpenRouter Key', order: 2 },
  { id: 'env_validated', label: 'Validate Environment', order: 3 },
  { id: 'pb_volume_created', label: 'Create PB Data Volume', order: 4 },
  { id: 'agi_volume_created', label: 'Create OpenClaw Data Volume', order: 5 },
  { id: 'n8n_volume_created', label: 'Create n8n Data Volume', order: 6 },
  { id: 'droplet_created', label: 'Create Droplet', order: 7 },
  { id: 'tenant_dns_configured', label: 'Configure Tenant DNS', order: 8 },
  { id: 'droplet_ready', label: 'Connect via SSH', order: 9 },
  { id: 'images_pulled', label: 'Pull Container Images', order: 10 },
  { id: 'stack_deployed', label: 'Deploy Stack', order: 11 },
  { id: 'health_check_passed', label: 'Run Health Checks', order: 12 },
  { id: 'tls_live', label: 'Verify TLS Certificate', order: 13 },
  { id: 'oauth_configured', label: 'Configure OAuth', order: 14 },
  { id: 'n8n_service_healthy', label: 'Verify n8n Service', order: 15 },
  { id: 'n8n_provisioned', label: 'Provision n8n', order: 16 },
  { id: 'provision_complete', label: 'Finalize Provision', order: 17 },
] as const satisfies readonly MilestoneDefinition[]

export const TEARDOWN_MILESTONES = [
  { id: 'containers_stopped', label: 'Stop Containers', order: 1 },
  { id: 'droplet_deleted', label: 'Delete Droplet', order: 2 },
  { id: 'dns_deleted', label: 'Remove DNS Record', order: 3 },
  { id: 'teardown_complete', label: 'Finalize Teardown', order: 4 },
] as const satisfies readonly MilestoneDefinition[]

export const UPDATE_MILESTONES = [
  { id: 'update_env_fetched', label: 'Fetch Environment Template', order: 1 },
  { id: 'update_env_validated', label: 'Validate Environment', order: 2 },
  { id: 'update_images_pulled', label: 'Pull Container Images', order: 3 },
  { id: 'update_services_restarted', label: 'Restart Services', order: 4 },
  { id: 'update_health_passed', label: 'Run Health Checks', order: 5 },
  { id: 'update_complete', label: 'Finalize Update', order: 6 },
] as const satisfies readonly MilestoneDefinition[]

export const REDEPLOY_MILESTONES = [
  { id: 'env_loaded', label: 'Load Environment', order: 1 },
  { id: 'droplet_created', label: 'Create Droplet', order: 2 },
  { id: 'tenant_dns_configured', label: 'Configure Tenant DNS', order: 3 },
  { id: 'droplet_ready', label: 'Connect via SSH', order: 4 },
  { id: 'stack_deployed', label: 'Deploy Stack', order: 5 },
  { id: 'health_check_passed', label: 'Run Health Checks', order: 6 },
  { id: 'tls_live', label: 'Verify TLS Certificate', order: 7 },
  { id: 'redeploy_complete', label: 'Finalize Redeploy', order: 8 },
] as const satisfies readonly MilestoneDefinition[]

/**
 * Derived: the union of every milestone id across all flows. Used by
 * `logger.milestone()` to constrain emissions to known ids only.
 */
export type MilestoneId =
  | typeof PROVISION_MILESTONES[number]['id']
  | typeof TEARDOWN_MILESTONES[number]['id']
  | typeof UPDATE_MILESTONES[number]['id']
  | typeof REDEPLOY_MILESTONES[number]['id']

// --- PocketBase Collection Names ---

export const COLLECTIONS = {
  TENANTS: 'tenants',
  TENANT_PROVISIONS: 'tenant_provisions',
  TENANT_PROVISION_ENV: 'tenant_provision_env',
  TENANT_SECRETS: 'tenant_secrets',
  PROVISION_LOGS: 'provision_logs',
  HEALTH_CHECKS: 'health_checks',
  JOBS: 'jobs',
  AUDIT_LOG: 'audit_log',
  SETTINGS: 'settings',
  PLATFORM_SECRETS: 'platform_secrets',
  USERS: 'users',
} as const
